/**
 * DeviceService - Facade for device creation operations
 * 
 * Coordinates device templates (definitions) and instances.
 * Provides high-level operations for:
 * - Creating new templates/definitions
 * - Instantiating devices from templates
 * - Combined create (template + instance in one call)
 */

import { generateId } from '../utils/index.js';
import { deepClone } from '../utils/helpers.js';
import { eventBus, EVENTS } from './EventBus.js';
import { findOpenPosition } from './LayoutService.js';
import { createDeviceRestoreFunctions } from './UndoManager.js';

/**
 * Create a DeviceService instance
 * 
 * @param {Object} options - Service dependencies
 * @param {Object} options.definitionModel - Device definition model
 * @param {Object} options.instanceModel - Device instance model
 * @param {Function} options.renderLibrary - Callback to re-render library UI
 * @param {Function} options.renderDevice - Callback to render a device on canvas
 * @param {Function} options.selectDevice - Callback to select a device
 * @param {Function} options.scheduleAutosave - Callback to trigger autosave
 * @param {Function} options.recordAction - Callback to record undo/redo action
 * @returns {Object} DeviceService API
 */
export function createDeviceService({
    definitionModel,
    instanceModel,
    renderLibrary,
    renderDevice,
    selectDevice,
    scheduleAutosave,
    recordAction
}) {
    /**
     * Create a new device template/definition
     * 
     * @param {string} name - Device name
     * @param {Array} portGroups - Port group configurations
     * @param {string} manufacturer - Manufacturer name
     * @param {string} version - Version string
     * @param {boolean} isRouter - Whether device is a router
     * @returns {Object} The created template
     */
    function createTemplate(name, portGroups, manufacturer = '', version = '', isRouter = false) {
        // Normalize all group fields (no UUIDs needed - groups identified by index)
        const normalizedGroups = (portGroups || []).map(g => {
            const normalized = {
                name: g.name,
                direction: g.direction,
                type: g.types && g.types.length > 1 ? g.types : (g.type || 'audio'),
                count: parseInt(g.count) || 1
            };
            // Include offset if not default (1)
            if (g.offset && g.offset !== 1) {
                normalized.offset = parseInt(g.offset);
            }
            // Include labels if provided
            if (g.labels && g.labels.trim()) {
                normalized.labels = g.labels.trim();
            }
            // Include stereoPairs if true
            if (g.stereoPairs) {
                normalized.stereoPairs = true;
            }
            // Include connector type if specified
            if (g.connector) {
                normalized.connector = g.connector;
            }
            return normalized;
        });

        const template = {
            id: generateId(),
            name,
            manufacturer: manufacturer || '',
            version: version || '',
            portGroups: normalizedGroups
        };
        
        // Only include isRouter if true (minimize data)
        if (isRouter) {
            template.isRouter = true;
        }
        
        definitionModel.add(template);
        renderLibrary();
        scheduleAutosave();
        return template;
    }

    /**
     * Create a device instance from an existing template
     * 
     * @param {string} templateId - Template ID to instantiate
     * @param {Object} position - Position {x, y} or null for auto-placement
     * @param {boolean} skipHistory - Skip undo/redo recording
     * @returns {Object|null} The created instance or null if template not found
     */
    function createInstanceFromTemplate(templateId, position, skipHistory = false) {
        const template = definitionModel.getById(templateId);
        if (!template) return null;
        
        const instance = {
            id: generateId(),
            templateId: template.id,
            position: position || findOpenPosition(instanceModel)
        };
        
        instanceModel.add(instance);
        renderDevice(instance);
        selectDevice(instance.id);
        scheduleAutosave();
        
        // Emit device added event (LibraryController subscribes → renderLibrary)
        eventBus.emit(EVENTS.DEVICE_ADDED, { device: instance, deviceId: instance.id });
        
        // Record action for undo/redo (unless skipped for batch operations)
        if (!skipHistory && recordAction) {
            const instanceCopy = deepClone(instance);
            
            // Use UndoManager helper - for add operations, undo = remove, redo = restore
            const { restoreDevice, removeDevice } = createDeviceRestoreFunctions(instanceCopy, []);
            
            recordAction(
                'add-device',
                `Add ${template.name}`,
                removeDevice,   // Undo: remove the device
                restoreDevice   // Redo: re-add the device
            );
        }
        
        return instance;
    }

    /**
     * Create both a template and an instance in one operation
     * 
     * @param {string} name - Device name
     * @param {Array} portGroups - Port group configurations
     * @param {Object} position - Position {x, y} or null for auto-placement
     * @param {string} manufacturer - Manufacturer name
     * @param {string} version - Version string
     * @param {boolean} isRouter - Whether device is a router
     * @returns {Object} The created instance
     */
    function createDevice(name, portGroups, position, manufacturer = '', version = '', isRouter = false) {
        // Create template first
        const template = createTemplate(name, portGroups, manufacturer, version, isRouter);
        
        // Then create instance
        return createInstanceFromTemplate(template.id, position);
    }

    // Return public API
    return {
        createTemplate,
        createInstanceFromTemplate,
        createDevice
    };
}
